import React from "react";
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { type } from "os";

type BtnProps = {
  type: "BLACK" | "YELLOW" | "OUTLINE";
  width?: string;
  height?: string;
  IconHeight?: string;
  iconSrc?: string;
  children?: React.ReactNode;
  isDisabled?: boolean;
  onClick?: () => void;
};

function Button(props: BtnProps) {
  if (props.type === "YELLOW") {
    return <YellowButton {...props} />;
  }
  if (props.type === "BLACK") {
    return <BlackButton {...props} />;
  }
  if (props.type === "OUTLINE") {
    return <OutlineButton {...props} />;
  }
  return <YellowButton {...props} />;
}

//Yellow Button
let YellowButton = ({
  type,
  width,
  height,
  IconHeight,
  iconSrc,
  children,
  onClick,
  isDisabled
}: BtnProps) => {
  return (
    <button
      disabled={isDisabled}
      onClick={onClick}
      css={css`
        display: flex;
        border: none;
        background: none;
        height: ${height || "47.53px"};
        width: ${width || "auto"};
        position: relative;
        cursor: pointer;
        transition: all 0.1s ease-in-out;
        &:hover {
          scale: 1.05;
        }
        &:disabled {
          opacity: .7;
        }
      `}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 251 49"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <path
          d="M0 14.145C0 7.275 5.748 1.799 12.611 2.07c10.787.428 26.802.944 41.175.944 24.505 0 41.98-1.502 66.485-1.502 24.505 0 43.515.952 67.979 0 10.521-.41 17.104-.367 27.64-.513 7.284-.101 16.47.041 23.619.201C245.921 1.344 251 6.6 251 13.012c0 4.491-.747 6.996-.747 11.487 0 3.2.095 7.04.217 10.658.228 6.766-5.045 12.478-11.814 12.6-16.196.293-46.471.742-79.54.742-24.505 0-62.75-2.5-97.857-.987-21.16.913-39.066.725-49.954.432C4.95 47.775 0 42.545 0 36.188V14.145Z"
          fill="#FFD550"
        />
      </svg>
      <span
        css={css`
          display: flex;
          width: 100%;
          height: 100%;
          position: absolute;
          left: 0;
          right: 0;
          justify-content: center;
          align-items: center;
          font-weight: 800;
          font-size: 16px;
          line-height: 22px;
          gap: 15px;
        `}
      >
        {!!iconSrc && (
          <img
            alt="btn-icon"
            css={css`
              height: ${IconHeight ? IconHeight : '75%'};
              object-fit: contain;
            `}
            src={iconSrc}
          />
        )}
        {children}
      </span>
    </button>
  );
};

//Black Button
let BlackButton = ({
  type,
  width,
  height,
  IconHeight,
  iconSrc,
  children,
  onClick,
  isDisabled
}: BtnProps) => {
  return (
    <button
      disabled={isDisabled}
      onClick={onClick}
      css={css`
        display: flex;
        border: none;
        background: none;
        height: ${height || "47.53px"};
        width: ${width || "auto"};
        position: relative;
        cursor: pointer;
        transition: all 0.1s ease-in-out;
        &:hover {
          scale: 1.05;
        }
        &:disabled {
          opacity: .7;
        }
      `}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 251 49"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <path
          d="M0 14.145C0 7.275 5.748 1.799 12.611 2.07c10.787.428 26.802.944 41.175.944 24.505 0 41.98-1.502 66.485-1.502 24.505 0 43.515.952 67.979 0 10.521-.41 17.104-.367 27.64-.513 7.284-.101 16.47.041 23.619.201C245.921 1.344 251 6.6 251 13.012c0 4.491-.747 6.996-.747 11.487 0 3.2.095 7.04.217 10.658.228 6.766-5.045 12.478-11.814 12.6-16.196.293-46.471.742-79.54.742-24.505 0-62.75-2.5-97.857-.987-21.16.913-39.066.725-49.954.432C4.95 47.775 0 42.545 0 36.188V14.145Z"
          fill="#000000"
        />
      </svg>
      <span
        css={css`
          display: flex;
          width: 100%;
          height: 100%;
          position: absolute;
          left: 0;
          right: 0;
          justify-content: center;
          align-items: center;
          font-weight: 800;
          font-size: 16px;
          line-height: 22px;
          color: #ffd550;
          gap: 15px;
        `}
      >
        {!!iconSrc && (
          <img
            alt="btn-icon"
            css={css`
              height: ${IconHeight ? IconHeight : '50%'};
              object-fit: contain;
              margin-right: 5px;
            `}
            src={iconSrc}
          />
        )}
        {children}
      </span>
    </button>
  );
};

//Outline Button
let OutlineButton = ({
  type,
  width,
  height,
  iconSrc,
  IconHeight,
  children,
  onClick,
  isDisabled
}: BtnProps) => {
  return (
    <button
      disabled={isDisabled}
      onClick={onClick}
      css={css`
        display: flex;
        border: none;
        background: none;
        height: ${height || "47.53px"};
        width: ${width || "auto"};
        position: relative;
        cursor: pointer;
        transition: all 0.1s ease-in-out;
        &:hover {
          scale: 1.05;
        }
        &:disabled {
          opacity: .7;
        }
      `}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 251 49"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <path
          d="M.75 14.145c0-6.44 5.39-11.58 11.831-11.324 10.79.427 26.817.944 41.205.944 12.266 0 22.772-.376 33.265-.751h.004c10.496-.376 20.978-.75 33.216-.75 8.361 0 16.092.11 23.625.219 14.504.209 28.271.407 44.383-.22 7.731-.301 13.327-.358 19.946-.425 2.378-.024 4.887-.05 7.675-.088 7.27-.101 16.445.041 23.593.201 5.998.135 10.757 5.052 10.757 11.062 0 2.202-.183 3.916-.369 5.664l-.001.009c-.187 1.747-.377 3.528-.377 5.814 0 3.212.095 7.062.217 10.684.215 6.359-4.74 11.71-11.077 11.825-16.194.292-46.464.74-79.527.74-8.484 0-18.651-.301-29.78-.632-20.901-.62-45.196-1.34-68.11-.353-21.137.911-39.025.724-49.9.432C5.387 47.036.75 42.152.75 36.188V14.145Z"
          stroke="#191414"
          stroke-width="1.5"
        />
      </svg>
      <span
        css={css`
          display: flex;
          width: 100%;
          height: 100%;
          position: absolute;
          left: 0;
          right: 0;
          justify-content: center;
          align-items: center;
          font-weight: 800;
          font-size: 16px;
          line-height: 22px;
          gap: 15px;
        `}
      >
        {!!iconSrc && (
          <img
            alt="btn-icon"
            css={css`
              height: ${IconHeight ? IconHeight : '75%'};
              object-fit: contain;
            `}
            src={iconSrc}
          />
        )}
        {children}
      </span>
    </button>
  );
};

export default Button;
